/**
 * ComingSoonPlaceholder — Shown to public visitors when a tenant's microsite
 * is in `draft` status. Keeps half-built sites off the public web until the
 * tenant explicitly hits "Publish".
 *
 * Admin bypass:
 *   • If a tenant JWT exists in localStorage ("access_token"), they skip the gate.
 *   • ?preview=true query param also bypasses (for quick share-links).
 *   • Regular visitors see the branded placeholder + optional email capture.
 *
 * SEO:
 *   • <meta name="robots" content="noindex, nofollow"> — never indexed.
 *   • <meta name="googlebot" content="noindex"> for double safety.
 *
 * Email capture:
 *   • POSTs to /api/public/site/prelaunch-subscribe (graceful no-op on error).
 */
import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Mail, Clock, Sparkles, CheckCircle2 } from "lucide-react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ComingSoonPlaceholder({ resolved }) {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const bizName = resolved?.name || "This Site";
  const logo = resolved?.logo_url || null;
  const tenantId = resolved?.tenant_id;
  const micrositeId = resolved?.microsite_id;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND}/api/public/site/prelaunch-subscribe`,
        { email, tenant_id: tenantId, microsite_id: micrositeId },
        { headers: { "Content-Type": "application/json" }, timeout: 8000 }
      );
      setSubscribed(true);
    } catch {
      // Graceful: still flip UI to "subscribed" — don't block user on network issues.
      setSubscribed(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-5 py-12 relative overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, #0f172a 0%, #1e1b4b 45%, #312e81 100%)",
      }}
      data-testid="coming-soon-placeholder"
    >
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
        <title>{bizName} — Coming Soon</title>
      </Helmet>

      {/* Subtle grain texture */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle, #fff 1px, transparent 1.5px)",
          backgroundSize: "24px 24px",
        }}
      />

      <div className="relative z-10 w-full max-w-lg text-center">
        {/* Logo */}
        {logo ? (
          <img
            src={logo}
            alt={bizName}
            className="h-16 w-16 rounded-2xl mx-auto mb-8 object-cover shadow-xl ring-1 ring-white/10"
          />
        ) : (
          <div className="h-16 w-16 rounded-2xl mx-auto mb-8 bg-white/10 border border-white/15 flex items-center justify-center backdrop-blur">
            <Sparkles className="w-7 h-7 text-indigo-200" />
          </div>
        )}

        {/* Badge */}
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 border border-white/15 backdrop-blur text-[11px] font-semibold tracking-wider uppercase text-indigo-200 mb-5">
          <Clock className="w-3 h-3" />
          Launching Soon
        </div>

        {/* Headline */}
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 leading-tight tracking-tight">
          {bizName}
          <br />
          <span className="text-indigo-300 font-normal italic">is on the way.</span>
        </h1>

        <p className="text-base text-slate-300 mb-10 max-w-md mx-auto leading-relaxed">
          We're putting the finishing touches on our booking site.
          Drop your email to get notified the moment we're live.
        </p>

        {/* Email capture */}
        {subscribed ? (
          <div
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 text-sm"
            data-testid="coming-soon-subscribed"
          >
            <CheckCircle2 className="w-4 h-4" />
            You're on the list! We'll be in touch.
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto"
            data-testid="coming-soon-form"
          >
            <div className="relative flex-1">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="coming-soon-email"
                className="w-full pl-10 pr-4 py-3 rounded-full bg-white/10 border border-white/15 text-white placeholder:text-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:bg-white/15 transition-all backdrop-blur"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="coming-soon-submit"
              className="px-6 py-3 rounded-full bg-white text-slate-900 text-sm font-bold hover:bg-indigo-100 active:scale-[0.98] transition-all disabled:opacity-50"
            >
              {submitting ? "…" : "Notify me"}
            </button>
          </form>
        )}

        {/* Footer */}
        <p className="mt-14 text-[11px] text-slate-500 tracking-wide">
          Powered by <span className="text-slate-400">PartyRentalSuite</span>
        </p>
      </div>
    </div>
  );
}
